import React from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { usePlatform } from '../../contexts/PlatformContext';
import type { ApplicationRecord } from '../../types/domain';
export type DecisionKind = 'approve' | 'reject' | 'request-info' | 'escalate';
const copy: Record<DecisionKind, {
  title: string;
  description: string;
  cta: string;
}> = {
  approve: {
    title: 'Approve at this stage',
    description: 'Records the approval decision and its rationale against the application audit timeline.',
    cta: 'Record approval'
  },
  reject: {
    title: 'Decline application',
    description: 'Declines the application. The entity is notified and may resubmit after remediation.',
    cta: 'Record decline'
  },
  'request-info': {
    title: 'Request information',
    description: 'Issues an information request to the applying entity and holds the record at its current stage.',
    cta: 'Send request'
  },
  escalate: {
    title: 'Escalate to committee',
    description: 'Refers the record to the federal alignment committee with your rationale attached.',
    cta: 'Record escalation'
  }
};
interface DecisionDialogProps {
  record: ApplicationRecord;
  kind: DecisionKind | null;
  onClose: () => void;
}
export function DecisionDialog({
  record,
  kind,
  onClose
}: DecisionDialogProps) {
  const {
    recordDecision
  } = usePlatform();
  const [rationale, setRationale] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (kind) {
      setRationale('');
      setError(null);
    }
  }, [kind]);
  if (!kind) return null;
  const config = copy[kind];
  function submit() {
    if (rationale.trim().length < 15) {
      setError('A decision rationale of at least 15 characters is required for the audit record.');
      return;
    }
    recordDecision(record.id, kind as DecisionKind, {
      rationale: rationale.trim()
    });
    onClose();
    toast.success(`${config.title} recorded for ${record.ref}`, {
      description: 'Decision, author and rationale added to the audit timeline.'
    });
  }
  return <Dialog open={Boolean(kind)} onOpenChange={next => !next ? onClose() : undefined} data-id="element-375" data-mp="cx0lxrv2pg9g">
      <DialogContent data-id="element-376" data-mp="rm6if82j0sdn">
        <DialogHeader data-id="element-377" data-mp="rln401c34m4d">
          <DialogTitle data-id="element-378" data-mp="rp2gcwedjh23">{config.title}</DialogTitle>
          <DialogDescription data-id="element-379" data-mp="moo75b11e12h">{config.description}</DialogDescription>
        </DialogHeader>

        <div className="rounded-sm border border-border bg-muted/50 px-3 py-2 text-[12px]" data-id="element-380" data-mp="10o5y9x1t1qf0">
          <span className="font-mono text-muted-foreground" data-id="element-381" data-mp="vo1kiesa79sh">{record.ref}</span> · {record.eventName}
        </div>

        <div className="space-y-1.5" data-id="element-382" data-mp="32e7se2bwtqag">
          <Label htmlFor="decision-rationale" data-id="element-383" data-mp="32f9agqrcfd1o">Decision rationale (required)</Label>
          <Textarea id="decision-rationale" rows={4} value={rationale} onChange={event => setRationale(event.target.value)} placeholder="State the basis for this decision, including any AI recommendation accepted or overridden" aria-invalid={Boolean(error)} data-id="element-384" data-mp="2xhh9l78q0glc" />
          {error && <p className="text-[12px] text-uae-red" data-id="element-385" data-mp="186x7iikj2qqv">{error}</p>}
        </div>

        <DialogFooter data-id="element-386" data-mp="184stv8wtudde">
          <Button variant="outline" size="sm" onClick={onClose} data-id="element-387" data-mp="u7nsvnbz04p0">
            Cancel
          </Button>
          <Button size="sm" variant={kind === 'reject' ? 'destructive' : 'default'} onClick={submit} data-id="element-388" data-mp="z70c7y3mutmu">
            {config.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}